import React, { useEffect, useState } from 'react';
import { Box, Chip, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SERVER_NAME } from '../config';

const ACTION_TYPE_STYLE = {
  PAPER_BUY:  { color: '#9ef7c5', bg: 'rgba(158,247,197,0.12)' },
  PAPER_SELL: { color: '#ff4d6a', bg: 'rgba(255,77,106,0.12)'  },
  PAPER_EXIT: { color: '#ff4d6a', bg: 'rgba(255,77,106,0.12)'  },
  REJECT:     { color: '#9ea4d8', bg: 'rgba(158,164,216,0.10)' },
  WAIT:       { color: '#ffe55c', bg: 'rgba(255,229,92,0.10)'  },
};

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return String(s); }
}
function fmt2(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}
function fmtUsd(v) {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : '—';
}

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: { padding: 12 },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  row: {
    borderRadius: 10,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '9px 12px',
    marginBottom: 8,
  },
  rowTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  rowText: {
    color: '#c8cff8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 3,
  },
  rowReject: {
    color: '#ffb347',
    fontSize: 11,
    lineHeight: 1.4,
    marginTop: 3,
  },
  meta: {
    color: '#8d93c9',
    fontSize: 11,
    marginTop: 4,
  },
  empty: {
    color: '#8d93c9',
    fontSize: 12,
    lineHeight: 1.45,
  },
  viewAll: {
    color: '#4d79ff',
    fontSize: 11,
    marginTop: 10,
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
    display: 'inline-block',
  },
}));

export default function AgentVerdictsTokenPanel({ address }) {
  const classes = useStyles();
  const [verdicts, setVerdicts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!address) return;
    setLoading(true);
    fetch(`${SERVER_NAME}/agent-verdicts?token_address=${encodeURIComponent(address)}&limit=5`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setVerdicts(data && data.verdicts ? data.verdicts : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [address]);

  if (!loading && !verdicts.length) return null;

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>Agent Verdicts</Typography>
      <Divider className={classes.divider} />

      {loading ? (
        <Typography className={classes.empty}>Loading verdicts…</Typography>
      ) : (
        verdicts.map((v) => {
          const atStyle = ACTION_TYPE_STYLE[v.action_type] || { color: '#cfd8ff', bg: 'rgba(207,216,255,0.08)' };
          const conf = Number(v.confidence_score);
          const metaParts = [];
          if (Number.isFinite(conf)) metaParts.push(`conf ${conf.toFixed(2)}`);
          if (v.entry_price && Number(v.entry_price) > 0) metaParts.push(`@ ${fmtUsd(v.entry_price)}`);
          if (v.allocation_usd && Number(v.allocation_usd) > 0) metaParts.push(`alloc ${fmtUsd(v.allocation_usd)}`);
          if (v.market_regime) metaParts.push(v.market_regime);

          return (
            <Box key={v.action_id} className={classes.row}>
              <Box className={classes.rowTop}>
                <Chip
                  size="small"
                  label={v.action_type || '—'}
                  style={{
                    height: 16, fontSize: 9, fontWeight: 900, textTransform: 'uppercase',
                    color: atStyle.color, backgroundColor: atStyle.bg,
                    border: `1px solid ${atStyle.color}`,
                  }}
                />
                <Typography style={{ fontSize: 11, color: '#9ea4d8' }}>{fmtDate(v.created_at)}</Typography>
              </Box>
              {v.thesis_summary ? (
                <Typography className={classes.rowText}>{v.thesis_summary}</Typography>
              ) : v.reason_summary ? (
                <Typography className={classes.rowText}>{v.reason_summary}</Typography>
              ) : null}
              {v.reject_reason ? (
                <Typography className={classes.rowReject}>{v.reject_reason}</Typography>
              ) : null}
              {metaParts.length ? (
                <Typography className={classes.meta}>{metaParts.join(' · ')}</Typography>
              ) : null}
            </Box>
          );
        })
      )}

      {!loading && verdicts.length > 0 ? (
        <a href="/agent-verdicts" className={classes.viewAll}>View all verdicts →</a>
      ) : null}
    </Box>
  );
}
